import React from 'react';
import isEqual from 'lodash.isequal';
import { DEFAULT_CONFIG, VISIBILITY_TYPES } from './savedSearchConstants';
import { SavedSearch, SavedSearchConfiguration, SavedSearchAPIResponse, SavedSearchAPIPayload, SelectOption } from '../types';

export const mergeConfigurations = (baseConfig: SavedSearchConfiguration, savedConfig: SavedSearchConfiguration | null): SavedSearchConfiguration => {
  if (!savedConfig) return baseConfig;

  return {
    ...baseConfig,
    filters: savedConfig.filters || baseConfig.filters,
    columns: {
      ...baseConfig.columns,
      ...savedConfig.columns
    },
    sorting: {
      ...baseConfig.sorting,
      ...savedConfig.sorting
    },
    pagination: {
      ...baseConfig.pagination,
      ...savedConfig.pagination
    },
    searchTerm: savedConfig.searchTerm || baseConfig.searchTerm || ''
  };
};

export const extractTableConfig = (tableState: any): SavedSearchConfiguration => {
  return {
    filters: tableState.filter || DEFAULT_CONFIG.filters,
    columns: {
      visible: tableState.visibleColumns || [],
      hidden: tableState.hiddenColumns || [],
      order: tableState.columnOrder || []
    },
    sorting: {
      orderBy: tableState.orderBy,
      order: tableState.order || 'asc'
    },
    pagination: {
      pageSize: tableState.rowsPerPage || 25
    },
    searchTerm: tableState.searchTerm || ''
  };
};

export const hasConfigurationChanges = (currentConfig: any, savedConfig: any): boolean => {
  if (!currentConfig || !savedConfig) return false;
  return !isEqual(currentConfig, savedConfig);
};

export const formatSavedSearchForAPI = (savedSearch: SavedSearch, profileType: string = 'SavedSearch'): SavedSearchAPIPayload => {
  const configuration = savedSearch.configuration || savedSearch;

  return {
    Name: savedSearch.name,
    Type: profileType,
    Profile: JSON.stringify({
      tableType: savedSearch.tableType,
      configuration: configuration,
      // Don't save isDefault to API - defaults are per-user, not global
      createdAt: savedSearch.createdAt || new Date().toISOString(),
      updatedAt: new Date().toISOString()
    }),
    Shared: savedSearch.visibility === VISIBILITY_TYPES.PARTNERS,
    Id: savedSearch.id || null
  };
};

export const parseSavedSearchFromAPI = (apiResponse: SavedSearchAPIResponse, tableType: string): SavedSearch => {
  let profile: any;
  try {
    profile = typeof apiResponse.Profile === 'string'
      ? JSON.parse(apiResponse.Profile)
      : apiResponse.Profile;
  } catch (e) {
    profile = { configuration: DEFAULT_CONFIG };
  }

  // If the profile has a 'configuration' key, use it (new format).
  // Otherwise, the entire profile IS the configuration (legacy format, e.g. Dashboard widgets).
  const configuration = profile.configuration !== undefined ? profile.configuration : profile;

  return {
    id: apiResponse.Id,
    name: apiResponse.Name,
    tableType: profile.tableType || tableType,
    configuration: configuration,
    // API searches with Shared:false are company, Shared:true would be partners (not supported)
    // Private searches don't come from API
    visibility: apiResponse.Shared ? VISIBILITY_TYPES.PARTNERS : VISIBILITY_TYPES.COMPANY,
    // Don't use isDefault from API - defaults are per-user, not global
    isDefault: false,
    createdAt: profile.createdAt,
    updatedAt: profile.updatedAt,
    createdBy: apiResponse.CreatedBy,
    partnerCompanyName: apiResponse.PartnerCompanyName
  };
};

export const formatSearchOption = (savedSearch: SavedSearch): SelectOption => {
  let displayName = savedSearch.name;
  let visibilityLabel = '';

  if (savedSearch.partnerCompanyName) {
    visibilityLabel = `created by ${savedSearch.partnerCompanyName}`;
  } else if (savedSearch.visibility === VISIBILITY_TYPES.PARTNERS) {
    visibilityLabel = 'shared with partners';
  } else if (savedSearch.visibility === VISIBILITY_TYPES.COMPANY) {
    visibilityLabel = 'visible to company';
  } else if (savedSearch.visibility === VISIBILITY_TYPES.PRIVATE) {
    visibilityLabel = 'visible to me';
  }

  if (savedSearch.isDefault) {
    displayName += ' ★';
  }

  // Create a React element for the label
  const labelElement = visibilityLabel ? (
    <span style={{ display: 'block', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
      <span>{displayName}</span>
      <span style={{
        color: '#666',
        fontSize: '0.9em',
        marginLeft: '4px'
      }}>
        ({visibilityLabel})
      </span>
    </span>
  ) : displayName;

  return {
    value: String(savedSearch.id),
    label: labelElement,
    search: savedSearch
  };
};

export const sortSavedSearches = (searches: SavedSearch[]): SavedSearch[] => {
  return [...searches].sort((a, b) => {
    // Default searches first
    if (a.isDefault !== b.isDefault) {
      return a.isDefault ? -1 : 1;
    }
    // Then by visibility (private, company, partners)
    const visibilityOrder: Record<string, number> = {
      [VISIBILITY_TYPES.PRIVATE]: 0,
      [VISIBILITY_TYPES.COMPANY]: 1,
      [VISIBILITY_TYPES.PARTNERS]: 2
    };
    if (a.visibility !== b.visibility) {
      return visibilityOrder[a.visibility] - visibilityOrder[b.visibility];
    }
    // Finally alphabetically
    return a.name.localeCompare(b.name);
  });
};

export const validateSearchName = (name: string, existingSearches: Array<{ name?: string } | string>): string | null => {
  if (!name || name.trim() === '') {
    return 'Name is required';
  }

  if (name.length > 100) {
    return 'Name must be less than 100 characters';
  }

  const duplicate = existingSearches.find(
    (search: any) => search.name && search.name.toLowerCase() === name.trim().toLowerCase()
  );

  if (duplicate) {
    return 'There is already another view with this name, please choose a different name.';
  }

  return null;
};

export const getDeleteConfirmationMessage = (savedSearch: SavedSearch): string => {
  if (savedSearch.visibility === VISIBILITY_TYPES.PARTNERS) {
    return 'Are you sure you want to delete this view? If this is a shared view, it will also be removed for everyone it was shared with. This action cannot be undone.';
  } else if (savedSearch.visibility === VISIBILITY_TYPES.COMPANY) {
    return 'Are you sure you want to delete this view? It will be removed for all users in your company. This action cannot be undone.';
  }
  return 'Are you sure you want to delete this view? This action cannot be undone.';
};
